import {connect} from 'react-redux';

import {createSelector, makeMapStateToProps} from '../../../../srcCommon/helpers/reselectHelpers';
import {StandaloneComposerStatusEnum} from '../../../helpers/composerHelper';
import {isMessageVisuallyShared} from '../../../helpers/models/messageHelpers';
import {MessageStatusesEnum, MessageVisualStatusesEnum} from '../../../models/api/messageModel';
import {AppState} from '../../../store/appState';
import {getConversations, getMessages} from '../../../store/data/dataSelectors';
import {getUserId} from '../../../store/data/teammates/teammatesSelectors';
import {getHasAutoShareDrafts} from '../../../store/featureFlags/featureFlagsSelectors';
import {panelIsComposerPanel} from '../../../store/panels/panelsState';
import {ComposerPanelId} from '../../../store/panels/panelStateBase';
import {StandaloneComposer} from './standaloneComposer';

/*
 * Props.
 */

interface StandaloneComposerContainerProps {
  panelId: ComposerPanelId;
}

/*
 * Selectors.
 */

function getMessageUid(state: AppState, props: StandaloneComposerContainerProps) {
  const panel = state.panels[props.panelId];
  if (panelIsComposerPanel(panel)) {
    return panel.messageUid;
  }
  return undefined;
}

function makeGetMessage() {
  return createSelector(
    [getUserId, getMessageUid, getMessages, getConversations, getHasAutoShareDrafts],
    (userId, messageUid, messages, conversations, hasAutoShareDrafts) => {
      if (!messageUid) {
        return undefined;
      }
      const message = messages[messageUid];
      if (
        !message ||
        message.visualStatus !== MessageVisualStatusesEnum.DRAFT ||
        message.status !== MessageStatusesEnum.DRAFT_COMPOSE
      ) {
        return undefined;
      }
      const isShared = isMessageVisuallyShared(
        message,
        conversations[message.conversationId],
        hasAutoShareDrafts,
      );

      if (isShared && message.authorId !== userId) {
        return undefined;
      }

      return message;
    },
  );
}

function makeGetMessageStatus() {
  return createSelector(
    [getUserId, getMessageUid, getMessages, getConversations, getHasAutoShareDrafts],
    (userId, messageUid, messages, conversations, hasAutoShareDrafts) => {
      if (!messageUid) {
        return StandaloneComposerStatusEnum.INVALID_DRAFT;
      }
      const message = messages[messageUid];
      if (!message) {
        return StandaloneComposerStatusEnum.DELETED;
      }

      // If we are sending or sent the message, display that we have sent the message.
      //  There may be a waiting period to actually send, but the standalone composer doesn't care
      if (
        message.status === MessageStatusesEnum.SENT ||
        message.status === MessageStatusesEnum.DRAFT_SENDING
      ) {
        return StandaloneComposerStatusEnum.SENT;
      }

      const isShared = isMessageVisuallyShared(
        message,
        conversations[message.conversationId],
        hasAutoShareDrafts,
      );

      if (
        message.status === MessageStatusesEnum.DRAFT_COMPOSE &&
        message.visualStatus === MessageVisualStatusesEnum.DRAFT &&
        isShared &&
        message.authorId !== userId
      ) {
        return StandaloneComposerStatusEnum.NOT_AUTHOR_SHARED;
      }

      return StandaloneComposerStatusEnum.VALID_DRAFT;
    },
  );
}

const mapStateToProps = makeMapStateToProps<StandaloneComposerContainerProps>()(() => ({
  message: makeGetMessage(),
  status: makeGetMessageStatus(),
}));

export const StandaloneComposerContainer = connect(mapStateToProps)(StandaloneComposer);
